import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles } from "@/mocks/articleData";

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <main className="pt-16">
        <section className="py-20 bg-background-50 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h1>
          <Link
            to="/articles"
            className="inline-block mt-4 px-6 py-2.5 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-all duration-200 cursor-pointer"
          >
            返回文章列表
          </Link>
        </section>
      </main>
    );
  }

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "优惠资讯", href: "/articles" },
    { label: article.title },
  ];

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://www.biyingyazhou.com/articles/${article.id}`}
        ogType="article"
      />
      <SchemaOrg
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt,
          "datePublished": article.publishDate,
          "author": {
            "@type": "Organization",
            "name": "必赢亚洲",
          },
          "publisher": {
            "@type": "Organization",
            "name": "必赢亚洲",
          },
          "url": `https://www.biyingyazhou.com/articles/${article.id}`,
          "inLanguage": "zh-CN",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.biyingyazhou.com/" },
              { "@type": "ListItem", "position": 2, "name": "优惠资讯", "item": "https://www.biyingyazhou.com/articles" },
              { "@type": "ListItem", "position": 3, "name": article.title },
            ],
          },
        }}
      />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-500/10 text-primary-600 mb-4">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {article.readCount.toLocaleString()} 次阅读
                </span>
              </div>
            </div>

            <div className="relative h-64 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose-custom text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex justify-between items-center">
              <Link
                to="/articles"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                返回列表
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/articles/${related.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-500/20 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {related.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{related.publishDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <style>{`
        .prose-custom h4 { font-family: var(--font-heading); font-size: 1.125rem; font-weight: 700; color: oklch(var(--foreground-900)); margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .prose-custom p { font-size: 0.9375rem; line-height: 1.8; color: oklch(var(--foreground-700)); margin-bottom: 1rem; }
      `}</style>
    </>
  );
}